import { supabase } from '@/lib/supabase';
import { Session } from '@supabase/supabase-js';
import { createContext, useState, useEffect, useRef, ReactNode } from 'react';
import {
  getAuthToken,
  setAuthToken,
  removeAuthToken,
  getRefreshToken,
  setRefreshToken,
  removeRefreshToken
} from '@/lib/cookies';
import { checkCookieFormat } from '@/lib/cookie-format';
import { linkIdentity, trackEvent } from '@/lib/analytics';

export interface AuthContextType {
  session: Session | null;
  isLoading: boolean;
  checkCookieAuth: () => Promise<boolean>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Module-level cache to persist session across StrictMode remounts
let cachedSession: Session | null = null;
let hasInitialized = false;

// สร้าง provider component
function AuthProvider({ children }: { children: ReactNode }) {
  // Initialize state from cache to prevent flash of null during StrictMode remount
  const [session, setSession] = useState<Session | null>(() => cachedSession);
  const [isLoading, setIsLoading] = useState(() => !hasInitialized);

  // Ref to prevent concurrent checkCookieAuth calls (race condition prevention)
  const isVerifyingRef = useRef(false);
  // Cache the result of a recent successful verification to prevent redundant calls
  const lastVerificationRef = useRef<{ result: boolean; timestamp: number } | null>(null);

  // Wrapper to update both state and cache
  const updateSession = (newSession: Session | null) => {
    console.log('[updateSession] Setting session to:', newSession ? 'EXISTS' : 'NULL');
    cachedSession = newSession;
    setSession(newSession);
  };

  /**
   * Check for authentication token in cookies and verify it
   * @returns True if a valid cookie auth token was found
   */
  const checkCookieAuth = async (): Promise<boolean> => {
    // Prevent concurrent verification calls (race condition prevention)
    if (isVerifyingRef.current) {
      // If we have a recent cached result, return it
      if (lastVerificationRef.current && Date.now() - lastVerificationRef.current.timestamp < 5000) {
        return lastVerificationRef.current.result;
      }
      return false;
    }

    isVerifyingRef.current = true;

    try {
      // Check for auth token in cookies
      const token = getAuthToken();
      const refreshToken = getRefreshToken();

      if (!token) {
        lastVerificationRef.current = { result: false, timestamp: Date.now() };
        return false;
      }

      // Try to verify token directly
      const { data: userData, error: userError } = await supabase.auth.getUser(token);

      if (!userError && userData.user) {
        // Token is still valid
        const { data: sessionData } = await supabase.auth.getSession();
        if (sessionData.session) {
          updateSession(sessionData.session);
          hasInitialized = true;
          setIsLoading(false);
          lastVerificationRef.current = { result: true, timestamp: Date.now() };
          return true;
        }
      }

      // If we have a refresh token, try to refresh the session
      if (refreshToken) {
        const { data, error } = await supabase.auth.refreshSession({
          refresh_token: refreshToken,
        });

        if (error || !data.session) {
          console.error('Failed to refresh session with cookie token:', error);
          removeAuthToken();
          removeRefreshToken();
          lastVerificationRef.current = { result: false, timestamp: Date.now() };
          return false;
        }

        // Update tokens in cookies
        setAuthToken(data.session.access_token);
        if (data.session.refresh_token) {
          setRefreshToken(data.session.refresh_token);
        }

        // Update session state
        updateSession(data.session);
        hasInitialized = true;
        setIsLoading(false);

        lastVerificationRef.current = { result: true, timestamp: Date.now() };
        return true;
      } else {
        // No refresh token available
        console.error('Cookie auth token verification failed and no refresh token available');
        removeAuthToken();
        lastVerificationRef.current = { result: false, timestamp: Date.now() };
        return false;
      }
    } catch (error) {
      console.error('Error verifying cookie auth token:', error);
      removeAuthToken();
      removeRefreshToken();
      lastVerificationRef.current = { result: false, timestamp: Date.now() };
      return false;
    } finally {
      isVerifyingRef.current = false;
    }
  };

  /**
   * Sign out the user and clear the auth token cookie
   */
  const signOut = async (): Promise<void> => {
    await supabase.auth.signOut();
    removeAuthToken();
    removeRefreshToken();
    updateSession(null);
  };

  useEffect(() => {
    // Generate unique ID for this effect instance to track StrictMode double-mounting
    const effectId = Math.random().toString(36).substring(7);
    console.log(`[${effectId}] useEffect MOUNT`);

    // Track if component is mounted to prevent state updates after unmount
    let isMounted = true;
    // Track if initialization is complete to prevent race conditions
    let isInitialized = false;

    // Temporary: Check cookie format to help debug
    checkCookieFormat();

    // Subscribe to auth state changes FIRST
    console.log(`[${effectId}] Setting up onAuthStateChange listener`);
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log(`[${effectId}] Auth state change event:`, event);
      console.log(`[${effectId}] - session:`, session ? 'EXISTS' : 'NULL');
      console.log(`[${effectId}] - isInitialized:`, isInitialized);
      console.log(`[${effectId}] - isMounted:`, isMounted);

      // Skip INITIAL_SESSION - we handle initial session via getSession() below
      // This prevents race conditions between the two initialization paths
      if (event === 'INITIAL_SESSION') {
        console.log(`[${effectId}] SKIPPING INITIAL_SESSION`);
        return;
      }

      // Only process events after initialization is complete
      if (!isInitialized) {
        console.log(`[${effectId}] SKIPPING - not initialized yet:`, event);
        return;
      }

      if (!isMounted) {
        console.log(`[${effectId}] SKIPPING - not mounted:`, event);
        return;
      }

      if (event === 'SIGNED_IN' && session) {
        console.log(`[${effectId}] Processing SIGNED_IN`);
        // When user signs in, store tokens in cookies using centralized utilities
        // (cookies.ts handles the correct domain and format)
        setAuthToken(session.access_token);
        if (session.refresh_token) {
          setRefreshToken(session.refresh_token);
        }
        updateSession(session);
        hasInitialized = true;
        setIsLoading(false);

        // Link anonymous identity to user and track sign-in conversion
        if (session.user?.id) {
          linkIdentity(session.user.id);
          trackEvent('conversion', 'user_signed_in', {
            user_id: session.user.id,
            metadata: { provider: session.user.app_metadata?.provider },
          });
        }
      } else if (event === 'SIGNED_OUT') {
        console.log(`[${effectId}] Processing SIGNED_OUT`);
        // When user signs out, remove tokens from cookies
        removeAuthToken();
        removeRefreshToken();
        updateSession(null);
        setIsLoading(false);
      } else if (event === 'TOKEN_REFRESHED' && session) {
        console.log(`[${effectId}] Processing TOKEN_REFRESHED`);
        // When token is refreshed, update cookies
        setAuthToken(session.access_token);
        if (session.refresh_token) {
          setRefreshToken(session.refresh_token);
        }
        updateSession(session);
        // Don't change isLoading for token refresh
      } else {
        console.log(`[${effectId}] UNHANDLED event:`, event, 'session:', session ? 'EXISTS' : 'NULL');
      }
    });

    async function initAuth() {
      console.log(`[${effectId}] initAuth START - hasInitialized:`, hasInitialized);

      // Skip re-initialization if already done (StrictMode remount)
      if (hasInitialized && cachedSession) {
        console.log(`[${effectId}] Already initialized with session, skipping re-init`);
        setIsLoading(false);
        return;
      }

      // First try to get session from Supabase
      console.log(`[${effectId}] Calling supabase.auth.getSession()...`);
      const { data: { session: supabaseSession } } = await supabase.auth.getSession();
      console.log(`[${effectId}] getSession returned:`, supabaseSession ? 'SESSION EXISTS' : 'NULL');

      if (!isMounted) {
        console.log(`[${effectId}] initAuth ABORT - component unmounted`);
        return;
      }

      if (supabaseSession) {
        console.log(`[${effectId}] Got supabase session:`, supabaseSession);
        // If we have a session, store the tokens in cookies for universal login
        setAuthToken(supabaseSession.access_token);
        if (supabaseSession.refresh_token) {
          setRefreshToken(supabaseSession.refresh_token);
        }

        // Check cookies right after setting
        setTimeout(() => {
          checkCookieFormat();
        }, 100);

        console.log(`[${effectId}] Calling updateSession with valid session`);
        updateSession(supabaseSession);
        hasInitialized = true;
        setIsLoading(false);
      } else {
        console.log(`[${effectId}] No supabase session, checking cookies...`);
        // If no session from Supabase, check cookies for a token
        const cookieAuthSuccess = await checkCookieAuth();
        console.log(`[${effectId}] checkCookieAuth returned:`, cookieAuthSuccess);

        if (!isMounted) {
          console.log(`[${effectId}] initAuth ABORT after cookie check - component unmounted`);
          return;
        }
        if (!cookieAuthSuccess) {
          // If no cookie token or verification failed
          console.log(`[${effectId}] No cookie auth, setting isLoading=false`);
          hasInitialized = true;
          setIsLoading(false);
        }
      }

      // Mark local initialization flag as complete (for event listener)
      isInitialized = true;
      console.log(`[${effectId}] initAuth COMPLETE - isInitialized=true, hasInitialized=`, hasInitialized);
    }

    initAuth();

    return () => {
      console.log(`[${effectId}] useEffect CLEANUP - setting isMounted=false`);
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  console.log('session', session);

  const value = {
    session,
    isLoading,
    checkCookieAuth,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export { AuthContext, AuthProvider };
